import Phaser from 'phaser';
import { DataManagerEntity, InstanceData, ResolvedEntityData } from './DataManagerEntity';
import { DataManagerCollision } from './DataManagerCollision';
import { Entity } from '../entities/base/Entity';
import { Unit } from '../entities/base/Unit';
import { Structure } from '../entities/base/Structure';
import { PowerUp } from '../entities/powerups/PowerUp';
import { Boss } from '../entities/base/Boss';
import { Bomb } from '../entities/hazards/Bomb';
import { BossBat } from '../entities/enemies/BossBat';
import { Farm1Cannon } from '../entities/structures/Farm1Cannon';
import { LayerDepths } from '../constants/enums';

// Import behaviors for entity setup
import { WalkerBehavior } from '../entities/behaviors/WalkerBehavior';
import { FlyerBehavior } from '../entities/behaviors/FlyerBehavior';
import { TurretBehavior } from '../entities/behaviors/TurretBehavior';
import { ShooterBehavior } from '../entities/behaviors/ShooterBehavior';
import { AbductableBehavior } from '../entities/behaviors/AbductableBehavior';
import { FlipBehavior } from '../entities/behaviors/FlipBehavior';
import { PinBehavior } from '../entities/behaviors/PinBehavior';
import { TransformBehavior } from '../entities/behaviors/TransformBehavior';
import { SolidBehavior } from '../entities/behaviors/SolidBehavior';
import { PlatformBehavior } from '../entities/behaviors/PlatformBehavior';
import { LootBehavior } from '../entities/behaviors/LootBehavior';
import { SpawnChildrenBehavior } from '../entities/behaviors/SpawnChildrenBehavior';

export type EntityClass = new (
  scene: Phaser.Scene,
  x: number,
  y: number,
  texture: string,
  config: Record<string, unknown>
) => Entity;

export interface EntityRegistry {
  [key: string]: EntityClass;
}

/**
 * EntityFactory - Creates entities from prefab data
 *
 * Handles:
 * - Entity class registry
 * - Creating entities from resolved prefab data
 * - Adding behaviors based on config
 */
export class EntityFactory {
  private scene: Phaser.Scene;
  private prefabManager: DataManagerEntity;
  private collisionManager: DataManagerCollision | null = null;
  private registry: EntityRegistry = {};
  /** Cache for case-insensitive animation key lookups */
  private animKeyCache: Map<string, string> = new Map();

  constructor(scene: Phaser.Scene, prefabManager: DataManagerEntity) {
    this.scene = scene;
    this.prefabManager = prefabManager;

    // Register default classes
    this.registerDefaults();
  }

  /**
   * Find animation key using case-insensitive search
   * Returns the actual animation key if found, or null if not found
   */
  private findAnimationKey(baseKey: string): string | null {
    // Check cache first
    const cached = this.animKeyCache.get(baseKey.toLowerCase());
    if (cached !== undefined) {
      return cached || null;
    }

    // Exact match
    if (this.scene.anims.exists(baseKey)) {
      this.animKeyCache.set(baseKey.toLowerCase(), baseKey);
      return baseKey;
    }

    // Case-insensitive search
    const lowerKey = baseKey.toLowerCase();
    const animKeys = this.scene.anims.anims.keys();
    for (const key of animKeys) {
      if (key.toLowerCase() === lowerKey) {
        this.animKeyCache.set(lowerKey, key);
        return key;
      }
    }

    // Not found - cache the miss
    this.animKeyCache.set(lowerKey, '');
    return null;
  }

  /**
   * Set collision data manager for applying hitboxes
   */
  setCollisionManager(manager: DataManagerCollision): void {
    this.collisionManager = manager;
  }

  /**
   * Register default entity classes
   */
  private registerDefaults(): void {
    // Base classes as fallbacks (keys must be lowercase per project rules)
    this.register('unit', Unit as unknown as EntityClass);
    this.register('structure', Structure as unknown as EntityClass);
    this.register('powerup', PowerUp as unknown as EntityClass);
    this.register('boss', Boss as unknown as EntityClass);
    // Hazards
    this.register('bomb', Bomb as unknown as EntityClass);
    // Enemies
    this.register('bossbat', BossBat as unknown as EntityClass);
    // Special structures
    this.register('farm1cannon', Farm1Cannon as unknown as EntityClass);
  }

  /**
   * Register an entity class
   */
  register(name: string, entityClass: EntityClass): void {
    this.registry[name] = entityClass;
  }

  /**
   * Register multiple entity classes
   */
  registerMany(classes: EntityRegistry): void {
    for (const [name, entityClass] of Object.entries(classes)) {
      this.register(name, entityClass);
    }
  }

  /**
   * Create entity from instance data (resolves prefab automatically)
   */
  createFromInstance(instanceData: InstanceData): Entity | null {
    const resolved = this.prefabManager.resolve(instanceData);
    if (!resolved) {
      return null;
    }
    return this.create(resolved);
  }

  /**
   * Create entity by entityId at given position
   * Uses SSOT from entities-overrides.json for all properties (flipY, hp, etc.)
   */
  createFromName(entityId: string, x: number, y: number): Entity | null {
    return this.createFromInstance({
      entityId,
      x,
      y,
    });
  }

  /**
   * Create entity from resolved prefab data
   */
  create(entityData: ResolvedEntityData): Entity | null {
    // Try to get specific class, fall back to category-based class
    let EntityClass = this.registry[entityData.class || entityData.type];

    if (!EntityClass) {
      // Fall back to category-based class
      EntityClass = this.getCategoryClass(entityData.category);
    }

    if (!EntityClass) {
      console.warn(`EntityFactory: Unknown entity type: "${entityData.type}"`);
      return null;
    }

    // For structures, preserve original C3 origin instead of forcing grounded
    // This ensures buildings appear at their original visual positions
    const adjustedY = entityData.y;

    // Create the entity
    const entity = new EntityClass(
      this.scene,
      entityData.x,
      adjustedY,
      entityData.texture,
      entityData as Record<string, unknown>
    );

    // IMPORTANT: Apply origin override FIRST, immediately after creation
    // This must happen BEFORE displaySize/hitbox transforms to ensure
    // position is interpreted correctly with the intended origin
    this.applyOriginOverride(entity, entityData);

    // Set depth for entities to be visible above background/floor layers
    entity.setDepth(LayerDepths.Entities);
    // Si el prefab trae depth manual, respetarlo (para casos puntuales)
    const manualDepth = (entityData as Record<string, unknown>).depth as number | undefined;
    if (manualDepth !== undefined) {
      entity.setDepth(manualDepth);
    }

    // Store entity ID if present
    if (entityData.id) {
      entity.entityId = entityData.id;
    }

    // Apply display size from instance data (C3 uses scaled sizes)
    this.applyDisplaySize(entity, entityData);

    // Apply explicit flip from entity data (SSOT overrides)
    this.applyFlip(entity, entityData);

    // Apply scale if defined in entity data
    this.applyScale(entity, entityData);

    // Add behaviors based on config
    this.addBehaviors(entity, entityData);

    // Apply hitbox from collision data (origin already set above)
    this.applyHitbox(entity, entityData);

    // Note: solid behavior now comes from entities-overrides.json via behaviors.solid
    // (SSOT: no longer reading from collision-c3data.json)

    // Debug log for structures
    // if (entityData.category === 'structure') {
    //   const struct = entity as Structure;
    //   console.log(`[EntityFactory] Structure created: ${entityData.texture}`, {
    //     initialFrame: entity.frame?.name,
    //     HP: `${struct.health?.hp}/${struct.health?.maxHp}`,
    //     useProRate: struct.health?.useProRate,
    //     totalFrames: struct.health?.totalFrames,
    //     allegiance: entity.allegiance,
    //   });
    // }

    return entity;
  }

  /**
   * Apply display size from instance data
   * C3 instances have width/height that may differ from texture size (scaled)
   */
  private applyDisplaySize(entity: Entity, entityData: ResolvedEntityData): void {
    const width = (entityData as Record<string, unknown>).width as number | undefined;
    const height = (entityData as Record<string, unknown>).height as number | undefined;

    if (width !== undefined && height !== undefined) {
      entity.setDisplaySize(Math.abs(width), Math.abs(height));

      // Handle flip if width or height are negative (C3 convention)
      if (width < 0) entity.setFlipX(true);
      if (height < 0) entity.setFlipY(true);
    }
  }

  /**
   * Apply explicit flipX/flipY from entity data (SSOT in overrides)
   * This is separate from negative width/height which is C3 convention
   */
  private applyFlip(entity: Entity, entityData: ResolvedEntityData): void {
    const data = entityData as Record<string, unknown>;
    const flipX = data.flipX as boolean | undefined;
    const flipY = data.flipY as boolean | undefined;

    if (flipX !== undefined) entity.setFlipX(flipX);
    if (flipY !== undefined) entity.setFlipY(flipY);
  }

  /**
   * Apply scale multiplier from entity data
   * Supports: scale (uniform), scaleX, scaleY (independent)
   */
  private applyScale(entity: Entity, entityData: ResolvedEntityData): void {
    const data = entityData as Record<string, unknown>;
    const uniformScale = data.scale as number | undefined;
    const scaleX = data.scaleX as number | undefined;
    const scaleY = data.scaleY as number | undefined;

    // scaleX/scaleY override uniform scale
    const finalScaleX = scaleX ?? uniformScale ?? 1;
    const finalScaleY = scaleY ?? uniformScale ?? 1;

    if (finalScaleX !== 1 || finalScaleY !== 1) {
      entity.setScale(finalScaleX, finalScaleY);
    }
  }

  /**
   * Apply hitbox from collision data
   * Note: Origin is already set by applyOriginOverride if specified in entityData
   */
  private applyHitbox(entity: Entity, entityData: ResolvedEntityData): void {
    if (!this.collisionManager) return;

    // Try texture name first, then type
    const textureName = entityData.texture;
    const typeName = entityData.type;

    // Check if origin was explicitly set in entity data (manual override)
    const data = entityData as Record<string, unknown>;
    const hasOriginOverride = data.originY !== undefined || data.originX !== undefined;

    // Units use grounded origin (center-bottom) for walking on floor
    // Structures preserve their original C3 origin to maintain visual positioning
    // Skip origin changes if origin was already explicitly set via applyOriginOverride
    const isUnit = entityData.category === 'unit';

    const options = {
      useGroundedOrigin: isUnit && !hasOriginOverride,  // Skip if origin override exists
      skipOrigin: hasOriginOverride,  // Tell collision manager to skip origin entirely
      hitboxWidthRatio: (data.hitbox as { widthRatio?: number } | undefined)?.widthRatio,
      hitboxHeightRatio: (data.hitbox as { heightRatio?: number } | undefined)?.heightRatio,
    };

    const applied = this.collisionManager.applyHitbox(entity, textureName, options) ||
                    this.collisionManager.applyHitbox(entity, typeName, options);

    if (applied) {
      // Debug log (only in development)
      // console.log(`[EntityFactory] Hitbox applied for: ${textureName || typeName}`);
    }
  }

  /**
   * Apply origin override from entity data
   * This allows manual overrides to take precedence over collision data
   *
   * For grounded entities (originY=1), we want the VISIBLE content bottom to touch the ground,
   * not the frame bottom (which may have transparent padding).
   *
   * Uses collision data to find where the actual content is within the frame.
   */
  private applyOriginOverride(entity: Entity, entityData: ResolvedEntityData): void {
    const data = entityData as Record<string, unknown>;
    const originY = data.originY as number | undefined;
    const originX = data.originX as number | undefined;

    // Only apply if originY is explicitly set in entity data
    if (originY !== undefined) {
      const currentOriginX = originX ?? entity.originX;

      // Check collision data to find where the visual content actually is
      // This accounts for transparent padding in the sprite frame
      let contentBottomRatio = 1.0; // Default: content fills entire frame

      if (this.collisionManager) {
        const textureName = entityData.texture;
        const collision = this.collisionManager.getCollision(textureName);

        if (collision?.defaultHitbox) {
          // Content bottom = offsetY + height
          // e.g., offsetY=0.4, height=0.49 → content ends at 0.89 (89% from top)
          contentBottomRatio = collision.defaultHitbox.offsetYRatio + collision.defaultHitbox.heightRatio;
        }
      }

      // Calculate the effective origin for the visual content
      // If content bottom is at 89% of frame, and we want originY=1.0 (grounded),
      // we use 0.89 as the actual origin so content bottom is at Y position
      const effectiveOriginY = originY === 1.0 ? contentBottomRatio : originY;

      // Apply the effective origin (no Y compensation needed - origin matches content)
      entity.setOrigin(currentOriginX, effectiveOriginY);
    }
  }

  /**
   * Get class based on category
   */
  private getCategoryClass(category: string): EntityClass | undefined {
    switch (category) {
      case 'unit':
        return this.registry['unit'];
      case 'structure':
        return this.registry['structure'];
      case 'collectible':
      case 'powerup':
        return this.registry['powerup'];
      case 'boss':
        return this.registry['boss'];
      case 'hazard':
        return this.registry['bomb'];
      default:
        return undefined;
    }
  }

  /**
   * Add behaviors to entity based on config
   */
  private addBehaviors(entity: Entity, entityData: ResolvedEntityData): void {
    const behaviors = entityData.behaviors || {};

    // Walker behavior
    if (behaviors.walker) {
      entity.addBehavior(new WalkerBehavior(entity, behaviors.walker as Record<string, unknown>));
    }

    // Flyer behavior
    if (behaviors.flyer) {
      entity.addBehavior(new FlyerBehavior(entity, behaviors.flyer as Record<string, unknown>));
    }

    // Turret behavior (rotating aiming) - MUST be before shooter (writes to state)
    if (behaviors.turret) {
      const turretConfig = {
        ...(behaviors.turret as Record<string, unknown>),
        range: (entityData as Record<string, unknown>).detectionRange ?? 700,
      };
      entity.addBehavior(new TurretBehavior(entity, turretConfig));
    }

    // Shooter behavior (reads from turret state if present)
    if (behaviors.shooter) {
      // DEBUG: Log the entityData to see which entity is getting shooter behavior
      console.log('[EntityFactory.addBehaviors] Adding shooter to:', {
        entityType: entity.entityType,
        prefab: entityData.type,
        texture: entityData.texture,
        shooterConfig: behaviors.shooter,
      });
      // Merge shooter config with detectionRange from prefab
      const shooterConfig = {
        ...(behaviors.shooter as Record<string, unknown>),
        detectionRange: (entityData as Record<string, unknown>).detectionRange ?? 0,
      };
      entity.addBehavior(new ShooterBehavior(entity, shooterConfig));
    }

    // Abductable behavior
    if (behaviors.abductable) {
      entity.addBehavior(new AbductableBehavior(entity, behaviors.abductable as Record<string, unknown>));
    }

    // Flip behavior (for automatic sprite flipping)
    if (behaviors.flip) {
      entity.addBehavior(new FlipBehavior(entity, behaviors.flip as Record<string, unknown>));
    }

    // Pin behavior (for attaching to another entity)
    // Note: Target is set later via PinBehavior.pinTo() when parent entity is known
    if (behaviors.pin) {
      entity.addBehavior(new PinBehavior(entity, behaviors.pin as Record<string, unknown>));
    }

    // Transform behavior (proximity-triggered transformation and entity spawning)
    // Used by: Farm1 (transforms into cannon turret when player approaches)
    if (behaviors.transform) {
      entity.addBehavior(new TransformBehavior(entity, behaviors.transform as Record<string, unknown>));
    }

    // Solid behavior (immovable, blocks tractor beam)
    if (behaviors.solid) {
      entity.addBehavior(new SolidBehavior(entity, behaviors.solid as Record<string, unknown>));
    }

    // Platform behavior (gravity, collide with solids)
    // Used by: entities that stand on ground (cows, humans, etc.)
    if (behaviors.platform) {
      entity.addBehavior(new PlatformBehavior(entity, behaviors.platform as Record<string, unknown>));
    }

    // Loot behavior (drops/powerups on death)
    // Note: Structure already adds LootBehavior automatically when drops are configured.
    // This is for explicit behavior config via JSON if needed.
    if (behaviors.loot) {
      entity.addBehavior(new LootBehavior(entity, behaviors.loot as Record<string, unknown>));
    }

    // SpawnChildren behavior (spawns child entities on creation)
    // Used by: CannonBase (spawns CannonCannon)
    // Note: spawn() must be called after entity is fully initialized - see triggerSpawnChildren()
    if (behaviors.spawnChildren) {
      entity.addBehavior(new SpawnChildrenBehavior(entity, behaviors.spawnChildren as Record<string, unknown>));
    }

    // Note: HealthBehavior is added automatically by Unit/Structure/Boss
    // Note: PhaseBehavior is added automatically by Boss
  }

  /**
   * Trigger SpawnChildrenBehavior.spawn() for an entity
   * Call this after entity is added to scene with physics
   */
  triggerSpawnChildren(entity: Entity): void {
    const spawner = entity.getBehavior(SpawnChildrenBehavior);
    if (spawner && !spawner.hasSpawnedChildren()) {
      spawner.spawn();
    }
  }

  /**
   * Create multiple entities from instance data
   */
  createMany(instances: InstanceData[]): Entity[] {
    const entities: Entity[] = [];

    for (const instance of instances) {
      const entity = this.createFromInstance(instance);
      if (entity) {
        entities.push(entity);
      }
    }

    return entities;
  }

  /**
   * Check if a class is registered
   */
  hasClass(name: string): boolean {
    return name in this.registry;
  }

  /**
   * Get all registered class names
   */
  getRegisteredClasses(): string[] {
    return Object.keys(this.registry);
  }
}
